// Orval custom mutator. Generated hooks call this so they inherit our
// cookie-based auth + 401 redirect behavior instead of using bare fetch.
import { getCookie } from '@/lib/cookies'
import { telemetry } from '@/telemetry'
import { ApiError, authEvents } from './http'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api'

// Is the caller currently holding a live (present + unexpired) session?
//
// A 401 means two very different things depending on the answer:
//  - No live session (no token, or an expired one) → the user is effectively
//    logged out; prompting them to log in is the correct response.
//  - Live session → the user IS authenticated as themselves. The API returns
//    401 (rather than 403) for owner-gated resources, so a 401 here just means
//    "this resource isn't yours." Bouncing an already-logged-in user to a login
//    modal is nonsensical — re-authenticating as themselves changes nothing —
//    so we must NOT prompt; we just surface the error to the caller.
function isSessionLive(token: string | null): boolean {
  if (!token) return false
  const parts = token.split('.')
  if (parts.length !== 3) return true // not a JWT we can read — a cookie still means logged-in
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number }
    if (typeof payload.exp !== 'number') return true // no expiry claim — treat presence as live
    return payload.exp * 1000 > Date.now()
  } catch {
    return true // can't parse expiry — a present cookie still means logged-in
  }
}

export interface OrvalRequestConfig {
  url: string
  method: string
  params?: Record<string, unknown>
  data?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

export async function fetcher<T>(config: OrvalRequestConfig): Promise<T> {
  const url = new URL(`${API_BASE}${config.url}`, window.location.origin)
  if (config.params) {
    for (const [k, v] of Object.entries(config.params)) {
      if (v === undefined || v === null) continue
      if (Array.isArray(v)) v.forEach((item) => url.searchParams.append(k, String(item)))
      else url.searchParams.set(k, String(v))
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(config.headers ?? {}),
  }
  const token = getCookie('AccessToken')
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url.toString(), {
    method: config.method,
    headers,
    body: config.data !== undefined ? JSON.stringify(config.data) : undefined,
    signal: config.signal,
  })

  if (response.status === 401) {
    // Only prompt login when there's genuinely no live session. With a live
    // session a 401 is a per-resource "not yours" — surface it, don't prompt.
    const sessionLive = isSessionLive(token)
    if (!sessionLive) {
      authEvents.dispatchEvent(
        new CustomEvent('unauthorized', {
          detail: { returnUrl: window.location.pathname + window.location.search },
        })
      )
    }
    throw new ApiError(401, { message: sessionLive ? 'Not authorized' : 'Session expired' })
  }

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.toLowerCase().includes('application/json')
  const body = isJson ? await response.json().catch(() => null) : null

  if (!response.ok) {
    // Report server faults only. 4xx (404/401/etc.) is expected application
    // flow — telemetry on it would be noise.
    if (response.status >= 500) {
      telemetry.trackException(new Error(`API ${response.status}: ${url.pathname}`), {
        status: String(response.status),
        url: url.toString(),
      })
    }
    const errorBody =
      body ??
      (isJson
        ? { message: `API error ${response.status}` }
        : { message: await response.text().catch(() => `API error ${response.status}`) })
    throw new ApiError(response.status, errorBody)
  }
  return body as T
}

export default fetcher
